import { useParams } from "react-router-dom";
import { isNotFound, usePartnerQuery, useSummaryQuery } from "@/store/api";
import { PageHeader } from "@/components/layout/AppShell";
import { MetricCard } from "@/components/ui/metric-card";
import { Term } from "@/components/ui/term";
import { ErrorState } from "@/components/ui/empty-state";
import { NotFound } from "@/pages/NotFound";
import { fmtDate, fmtMoney, fmtNum, fmtPeriod } from "@/lib/format";

/** The partner's home: this Billing Period's figures, each beside last period's. */
export function Overview() {
  const { slug = "" } = useParams();
  const { data: partner } = usePartnerQuery(slug);
  const { data: s, isError, error, refetch } = useSummaryQuery(slug);

  if (isError && isNotFound(error)) return <NotFound />;
  if (isError) return <ErrorState onRetry={refetch} />;

  const prev = (v: string) =>
    `${s ? fmtPeriod(s.previousPeriod) : "Last period"}: ${v}`;
  const e = s?.entitlements;
  const smb = partner?.offers.smb ?? false;
  const agency = partner?.offers.agency ?? true;

  return (
    <>
      <PageHeader
        title={partner?.name ?? "Overview"}
        description={
          s ? (
            <>
              <Term id="billing-period">Billing Period</Term>{" "}
              {fmtPeriod(s.period)} (UTC). Figures update as licences, sites and
              transactions change.
            </>
          ) : undefined
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {agency && (
          <>
            <MetricCard
              label={<Term id="licence-entitlement">Licence Entitlements</Term>}
              value={e ? fmtNum(e.held) : "…"}
              caption={
                e && s ? (
                  <>
                    {fmtMoney(e.rateCents, "USD")} each this period
                    {e.distanceToNextTier !== null
                      ? ` · ${fmtNum(e.distanceToNextTier)} more for ${fmtMoney(750, "USD")}`
                      : " · lowest tier"}
                    {e.scheduledDecrease &&
                      ` · ${fmtNum(e.scheduledDecrease.to)} from ${fmtDate(e.scheduledDecrease.effectiveAt)}`}
                    <br />
                    {prev(fmtNum(s.previousHeld))}
                  </>
                ) : undefined
              }
            />
            <MetricCard
              label={<Term id="licence-granted">Licences granted</Term>}
              value={s ? fmtNum(s.granted.current) : "…"}
              caption={
                e && s ? (
                  <>
                    {e.returning > 0 &&
                      `${fmtNum(e.returning)} ended this period, returning ${fmtDate(e.lapsesAt)} · `}
                    {prev(fmtNum(s.granted.previous))}
                  </>
                ) : undefined
              }
            />
            <MetricCard
              label={<Term id="undrawn-entitlement">Undrawn Entitlements</Term>}
              value={s ? fmtNum(s.undrawn.current) : "…"}
              caption={
                e && s ? (
                  <>
                    Lapse {fmtDate(e.lapsesAt)} ·{" "}
                    {prev(fmtNum(s.undrawn.previous))}
                  </>
                ) : undefined
              }
            />
            <MetricCard
              label="Pending activation"
              value={s ? fmtNum(s.pending) : "…"}
              caption={
                s && s.pendingExpired > 0 ? (
                  <span className="text-ata-orange">
                    {fmtNum(s.pendingExpired)} with expired links — they no
                    longer hold an Entitlement
                  </span>
                ) : (
                  "Licences created but not yet activated"
                )
              }
            />
          </>
        )}
        {smb && (
          <MetricCard
            label={
              <>
                <Term id="smb-owner">SMB owners</Term>
                {s ? ` · ${fmtNum(s.smbSites)} sites` : ""}
              </>
            }
            value={
              s
                ? `${fmtNum(s.smbFree.current)} free · ${fmtNum(s.smbPaid.current)} paid`
                : "…"
            }
            caption={
              s
                ? prev(
                    `${fmtNum(s.smbFree.previous)} free · ${fmtNum(s.smbPaid.previous)} paid`,
                  )
                : undefined
            }
          />
        )}
        <MetricCard
          label={
            <>
              <Term id="expansion-charge">Expansion Charges</Term>
              {s && !s.currentPeriodClosed && " (provisional)"}
            </>
          }
          value={s ? fmtMoney(s.expansionCharges.current, s.currency) : "…"}
          caption={
            s ? (
              <>
                {s.currentPeriodClosed
                  ? "Final — period closed"
                  : "Running total; final once the period closes"}
                <br />
                {prev(fmtMoney(s.expansionCharges.previous, s.currency))}
              </>
            ) : undefined
          }
        />
      </div>
    </>
  );
}
